import { NewTrackedFile, SnapshotRevisionSearchItem, TrackedFileSpecifierType } from "@istari/istari-client";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { queryClient } from "~/api/queryClient";
import { DEFAULT_CACHE_TIME } from "~/constants";
import { ModelArtifactArchivedStatus, SystemFileRevisionSpecifierType, SystemResourceType } from "~/enums";
import { getFileNameAndExtension, stripExtensionFromDisplayName } from "~/utils/file";

import clientSDK from "../clientSDK";
import { ConfigurationTrackedFile } from "../configurations";
import { ModelProperties } from "../useModels";

export const snapshotFilesQueryKeys = {
  all: ["snapshotFiles"],
  revisions: (snapshotId: string) => [...snapshotFilesQueryKeys.all, "revisions", snapshotId],
  trackedFiles: (configId: string) => [...snapshotFilesQueryKeys.all, "trackedFiles", configId],
};

export type SnapshotFileAccessible = {
  _tag: "accessible";
  id: string;
  revisionId: string;
  resourceId: string;
  fileId: string;
  displayFileName: string;
  extension?: string;
  type: string;
  archiveStatus: ModelArtifactArchivedStatus;
  // True when the file has been revoked: content deleted and metadata zeroed.
  // Content/edit operations against a tombstoned resource return 410 Gone.
  tombstoned: boolean;
  snapshotId?: string;
  created?: Date;
  isNested: boolean;
  parentModelRevisionId?: string;
  hasNestedItems: boolean;
  properties: ModelProperties;
  contentToken?: {
    sha: string;
    salt: string;
  };
  jobSourceId?: string;
  /** Encoded, system-rooted folder this file sits in (its tracked-file path minus the file-id leaf). */
  folderPath?: string;
};

export type SnapshotFileInaccessible = {
  _tag: "inaccessible";
  id: string;
  revisionId: string;
  fileId: string;
  snapshotId?: string;
  isNested: boolean;
  parentModelRevisionId?: string;
};

export type SnapshotFileLoading = {
  _tag: "loading";
  id: string;
  revisionId: string;
  fileId: string;
  snapshotId?: string;
  isNested: boolean;
  parentModelRevisionId?: string;
};

export type SnapshotFile = SnapshotFileAccessible | SnapshotFileInaccessible | SnapshotFileLoading;

export type SnapshotFileGroup = {
  parent: SnapshotFile;
  nestedItems: SnapshotFile[];
  hasNestedItems: boolean;
  nestedRevisionIds?: string[];
  nestedResourceIdToRevisionId?: Record<string, string>;
};

export function getRevisionIdFromFile(file: SnapshotFile): string {
  return file.revisionId;
}

export function isAccessible(file: SnapshotFile): file is SnapshotFileAccessible {
  return file._tag === "accessible";
}

export function isInaccessible(file: SnapshotFile): file is SnapshotFileInaccessible {
  return file._tag === "inaccessible";
}

export function isFileLoading(file: SnapshotFile): file is SnapshotFileLoading {
  return file._tag === "loading";
}

// A tracked-file path is `<sysHex>.<folder labels…>.<fileIdHex>`; its folder is everything but the
// trailing file-id leaf. A file at the system root yields just `<sysHex>`.
function folderPathFromTrackedPath(path: string | undefined): string | undefined {
  if (!path) return undefined;
  const segments = path.split(".");
  return segments.length <= 1 ? undefined : segments.slice(0, -1).join(".");
}

export function createAccessibleFile(
  revisionData: SnapshotRevisionSearchItem,
  snapshotId: string | undefined,
  isNested: boolean,
  parentModelRevisionId?: string,
): SnapshotFileAccessible {
  const extension = revisionData.extension || "";
  const displayFileName =
    stripExtensionFromDisplayName(revisionData.displayName, extension) ||
    getFileNameAndExtension(revisionData.name || "")?.name ||
    "Unknown";
  const isModel = revisionData.resourceType === SystemResourceType.Model;

  return {
    _tag: "accessible",
    id: revisionData.revisionId,
    revisionId: revisionData.revisionId,
    resourceId: revisionData.resourceId || "",
    fileId: revisionData.fileId || "",
    displayFileName,
    extension,
    type: revisionData.resourceType || "",
    archiveStatus: revisionData.isArchived ? ModelArtifactArchivedStatus.Archived : ModelArtifactArchivedStatus.Active,
    tombstoned: revisionData.isTombstoned ?? false,
    snapshotId,
    created: revisionData.created,
    isNested,
    parentModelRevisionId,
    hasNestedItems: isModel && Boolean(revisionData.hasArtifacts),
    properties: {
      displayFileName,
      extension,
    },
    contentToken: revisionData.contentToken
      ? {
          sha: revisionData.contentToken.sha,
          salt: revisionData.contentToken.salt,
        }
      : undefined,
  };
}

export function createInaccessibleFile(
  fileId: string,
  revisionId: string,
  snapshotId: string | undefined,
  isNested: boolean = false,
  parentModelRevisionId?: string,
): SnapshotFileInaccessible {
  return {
    _tag: "inaccessible",
    id: fileId,
    revisionId,
    fileId,
    snapshotId,
    isNested,
    parentModelRevisionId,
  };
}

function createInaccessibleGroup(
  fileId: string,
  revisionId: string,
  snapshotId: string | undefined,
): SnapshotFileGroup {
  return {
    parent: createInaccessibleFile(fileId, revisionId, snapshotId, false),
    nestedItems: [],
    hasNestedItems: false,
    nestedRevisionIds: [],
    nestedResourceIdToRevisionId: {},
  };
}

export function buildSnapshotFileGroups(
  snapshotRevisions: SnapshotRevisionSearchItem[],
  trackedFiles: ConfigurationTrackedFile[],
  snapshotId?: string,
  isRevisionsComplete: boolean = true,
  // Generic file resources (notebooks etc.) serialize as SystemResourceType.File ("Resource").
  // They're only rendered in the tree when the Notebooks feature is on; off by default so the
  // standard system view keeps surfacing only models + artifacts.
  includeFileResources: boolean = false,
): SnapshotFileGroup[] {
  const allowedResourceTypes = new Set([SystemResourceType.Model, SystemResourceType.Artifact]);
  if (includeFileResources) allowedResourceTypes.add(SystemResourceType.File);

  const trackedFileIds = new Set(trackedFiles.map((f) => f.fileId));
  const trackedFileMap = new Map(trackedFiles.map((f) => [f.fileId, f]));
  const revisionFileIds = new Set(snapshotRevisions.filter((rev) => rev.fileId).map((rev) => rev.fileId as string));

  const topLevelRevisions = snapshotRevisions.filter((rev) => {
    if (!allowedResourceTypes.has(rev.resourceType as SystemResourceType)) {
      return false;
    }
    return rev.fileId ? trackedFileIds.has(rev.fileId) : false;
  });

  const groups: SnapshotFileGroup[] = topLevelRevisions.map((revisionData) => {
    const file = createAccessibleFile(revisionData, snapshotId, false, undefined);
    const trackedPath = revisionData.fileId ? trackedFileMap.get(revisionData.fileId)?.path : undefined;

    return {
      parent: { ...file, folderPath: folderPathFromTrackedPath(trackedPath) },
      nestedItems: [],
      hasNestedItems: file.hasNestedItems,
      nestedRevisionIds: undefined,
      nestedResourceIdToRevisionId: undefined,
    };
  });

  const unmatchedFileIds = Array.from(trackedFileIds).filter((fileId) => !revisionFileIds.has(fileId));
  const unmatchedGroups: SnapshotFileGroup[] = unmatchedFileIds.map((fileId) => {
    const trackedFile = trackedFileMap.get(fileId);
    const revisionId = trackedFile?.currentFileRevisionId || fileId;
    if (isRevisionsComplete) {
      return createInaccessibleGroup(fileId, revisionId, snapshotId);
    }
    return {
      parent: { _tag: "loading" as const, id: fileId, revisionId, fileId, snapshotId, isNested: false },
      nestedItems: [],
      hasNestedItems: false,
    };
  });

  const allGroups = [...groups, ...unmatchedGroups];

  return allGroups.sort((a, b) => {
    const aName = isAccessible(a.parent)
      ? a.parent.displayFileName
      : isInaccessible(a.parent)
        ? a.parent.revisionId
        : "";
    const bName = isAccessible(b.parent)
      ? b.parent.displayFileName
      : isInaccessible(b.parent)
        ? b.parent.revisionId
        : "";
    return aName.localeCompare(bName);
  });
}

function buildRevisionToFileIdMap(trackedFiles: ConfigurationTrackedFile[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const file of trackedFiles) {
    map.set(file.currentFileRevisionId, file.fileId);
  }
  return map;
}

export function findActiveSnapshotFileId(
  groups: SnapshotFileGroup[],
  targetResourceId: string | undefined | null,
  fallbackItemId: string | undefined | null,
  nestedItemsState?: Map<string, { files: SnapshotFile[]; hasMore: boolean; totalCount: number }>,
): string | null {
  if (!targetResourceId) return fallbackItemId || null;

  for (const group of groups) {
    if (isAccessible(group.parent) && group.parent.resourceId === targetResourceId) {
      return group.parent.id;
    }
    if (isInaccessible(group.parent) && group.parent.revisionId === targetResourceId) {
      return group.parent.id;
    }

    const nestedLoaded = nestedItemsState?.get(group.parent.revisionId)?.files || group.nestedItems;
    if (nestedLoaded) {
      const nestedMatch = nestedLoaded.find((n) => isAccessible(n) && n.resourceId === targetResourceId);
      if (nestedMatch) {
        return nestedMatch.id;
      }
    }
  }
  return fallbackItemId || null;
}

function paginateArray<T>(
  items: T[],
  offset: number,
  limit: number,
): { items: T[]; totalCount: number; hasMore: boolean } {
  const totalCount = items.length;
  const hasMore = offset + limit < totalCount;
  const paginatedItems = items.slice(offset, offset + limit);

  return {
    items: paginatedItems,
    totalCount,
    hasMore,
  };
}

export async function loadNestedSnapshotFiles(
  parentRevisionId: string,
  nestedRevisionIds: string[],
  snapshotRevisions: SnapshotRevisionSearchItem[],
  snapshotId: string | undefined,
  options?: { offset?: number; limit?: number; revisionToJobSourceId?: Record<string, string> },
): Promise<{
  files: SnapshotFile[];
  totalCount: number;
  hasMore: boolean;
}> {
  const { offset = 0, limit = 10, revisionToJobSourceId } = options ?? {};

  if (!nestedRevisionIds || nestedRevisionIds.length === 0) {
    return {
      files: [],
      totalCount: 0,
      hasMore: false,
    };
  }

  const snapshotRevisionMap = new Map(snapshotRevisions.map((rev) => [rev.revisionId, rev]));
  const validRevisionIds = nestedRevisionIds.filter((revisionId) => snapshotRevisionMap.has(revisionId));
  const { items: pageItems, totalCount, hasMore } = paginateArray(validRevisionIds, offset, limit);

  const files: SnapshotFile[] = pageItems.map((revisionId) => {
    const revisionData = snapshotRevisionMap.get(revisionId);

    if (!revisionData) {
      return createInaccessibleFile(revisionId, revisionId, snapshotId, true, parentRevisionId);
    }

    const file = createAccessibleFile(revisionData, snapshotId, true, parentRevisionId);
    if (revisionToJobSourceId && revisionToJobSourceId[revisionId]) {
      file.jobSourceId = revisionToJobSourceId[revisionId];
    }
    return file;
  });

  files.sort((a, b) => {
    const aIsInaccessible = isInaccessible(a);
    const bIsInaccessible = isInaccessible(b);
    if (aIsInaccessible !== bIsInaccessible) return aIsInaccessible ? 1 : -1;

    const aName = isAccessible(a) ? a.displayFileName : a.revisionId;
    const bName = isAccessible(b) ? b.displayFileName : b.revisionId;
    return aName.localeCompare(bName);
  });

  return {
    files,
    totalCount,
    hasMore,
  };
}

export type SnapshotRevisionsData = {
  items: SnapshotRevisionSearchItem[];
  isComplete: boolean;
};

export const snapshotRevisionsQueryOptions = (snapshotId: string) =>
  queryOptions({
    queryKey: snapshotFilesQueryKeys.revisions(snapshotId),
    queryFn: async ({ signal }): Promise<SnapshotRevisionsData> => {
      const firstResponse = await clientSDK.listSnapshotRevisions(snapshotId, 1, 100);
      if (!firstResponse.total || firstResponse.total <= 100) {
        return { items: firstResponse.items, isComplete: true };
      }

      const totalPages = firstResponse.pages || Math.ceil(firstResponse.total / 100);
      const queryKey = snapshotFilesQueryKeys.revisions(snapshotId);

      // Return the first page immediately so the tree renders resolved items.
      // Fetch remaining pages in parallel and update cache once when all arrive.
      const allItems = [...firstResponse.items];
      const pagePromises = [];
      for (let page = 2; page <= totalPages; page++) {
        pagePromises.push(clientSDK.listSnapshotRevisions(snapshotId, page, 100));
      }
      void Promise.all(pagePromises)
        .then((remainingPages) => {
          if (signal.aborted) return;
          remainingPages.forEach((response) => allItems.push(...response.items));
          queryClient.setQueryData<SnapshotRevisionsData>(queryKey, {
            items: [...allItems],
            isComplete: true,
          });
        })
        .catch((err) => {
          if (!signal.aborted) {
            console.error("Failed to fetch remaining snapshot revision pages:", err);
          }
        });

      return { items: firstResponse.items, isComplete: false };
    },
    staleTime: DEFAULT_CACHE_TIME,
  });

async function fetchTrackedFiles(configId: string): Promise<ConfigurationTrackedFile[]> {
  const firstResponse = await clientSDK.listTrackedFiles(configId, 1, 100);
  if (!firstResponse.total || firstResponse.total <= 100) {
    return firstResponse.items.map((item) => ({
      id: item.id,
      fileId: item.fileId,
      currentFileRevisionId: item.currentFileRevisionId,
      pinnedFileRevisionId: item.pinnedFileRevisionId,
      specifierType: item.specifierType,
      archiveStatus: item.archiveStatus,
      path: item.path,
    }));
  }

  const allItems = [...firstResponse.items];
  const totalPages = firstResponse.pages || Math.ceil(firstResponse.total / 100);

  const pagePromises = [];
  for (let page = 2; page <= totalPages; page++) {
    pagePromises.push(clientSDK.listTrackedFiles(configId, page, 100));
  }

  const remainingPages = await Promise.all(pagePromises);
  remainingPages.forEach((pageResponse) => {
    allItems.push(...pageResponse.items);
  });

  return allItems.map((item) => ({
    id: item.id,
    fileId: item.fileId,
    currentFileRevisionId: item.currentFileRevisionId,
    pinnedFileRevisionId: item.pinnedFileRevisionId,
    specifierType: item.specifierType,
    archiveStatus: item.archiveStatus,
    path: item.path,
  }));
}

export type UseSnapshotFilesResult = {
  groups: SnapshotFileGroup[];
  snapshotRevisions: SnapshotRevisionSearchItem[];
  trackedFiles: ConfigurationTrackedFile[];
  revisionToFileIdMap: Map<string, string>;
  isLoading: boolean;
  isRevisionsComplete: boolean;
  error: Error | null;
};

export function useSnapshotFiles(
  snapshotId: string | undefined,
  configId: string | undefined,
  includeFileResources: boolean = false,
): UseSnapshotFilesResult {
  const trackedFilesQuery = useQuery({
    queryKey: snapshotFilesQueryKeys.trackedFiles(configId!),
    queryFn: () => fetchTrackedFiles(configId!),
    enabled: !!configId,
    staleTime: Infinity,
  });

  const revisionsQuery = useQuery({
    ...snapshotRevisionsQueryOptions(snapshotId!),
    enabled: !!snapshotId,
  });

  const groups = useMemo(() => {
    if (!revisionsQuery.data || !trackedFilesQuery.data) {
      return [];
    }
    return buildSnapshotFileGroups(
      revisionsQuery.data.items,
      trackedFilesQuery.data,
      snapshotId,
      revisionsQuery.data.isComplete,
      includeFileResources,
    );
  }, [revisionsQuery.data, trackedFilesQuery.data, snapshotId, includeFileResources]);

  // Keep this map referentially stable so route/row callbacks do not churn across unrelated page renders.
  const revisionToFileIdMap = useMemo(
    () => (trackedFilesQuery.data ? buildRevisionToFileIdMap(trackedFilesQuery.data) : new Map<string, string>()),
    [trackedFilesQuery.data],
  );

  return {
    groups,
    snapshotRevisions: revisionsQuery.data?.items ?? [],
    trackedFiles: trackedFilesQuery.data || [],
    revisionToFileIdMap,
    isLoading: trackedFilesQuery.isLoading || revisionsQuery.isLoading,
    isRevisionsComplete: revisionsQuery.data?.isComplete ?? false,
    error: trackedFilesQuery.error || revisionsQuery.error,
  };
}

export type TrackedFileFromGroup = {
  type?: string;
  displayFileName?: string;
  extension?: string;
  fileId: string;
  modelId?: string;
  resourceId?: string;
  revisionId: string;
  currentRevisionId?: string;
  pinnedRevisionId?: string;
  status?: string;
  created?: Date;
  noAccess: boolean;
  specifierType: SystemFileRevisionSpecifierType;
};

export function snapshotFileGroupsToTrackedFiles(
  groups: SnapshotFileGroup[],
  trackedFiles: ConfigurationTrackedFile[],
): TrackedFileFromGroup[] {
  const trackedFileMap = new Map(trackedFiles.map((f) => [f.fileId, f]));

  return groups.map((group) => {
    const trackedFile = trackedFileMap.get(group.parent.fileId);
    const specifierType =
      trackedFile?.specifierType === TrackedFileSpecifierType.Locked
        ? SystemFileRevisionSpecifierType.Locked
        : SystemFileRevisionSpecifierType.Latest;

    if (isAccessible(group.parent)) {
      return {
        type: group.parent.type,
        displayFileName: group.parent.displayFileName,
        extension: group.parent.extension,
        fileId: group.parent.fileId,
        modelId: group.parent.resourceId,
        resourceId: group.parent.resourceId,
        revisionId: group.parent.revisionId,
        currentRevisionId: group.parent.revisionId,
        pinnedRevisionId: trackedFile?.pinnedFileRevisionId || group.parent.revisionId,
        status: group.parent.archiveStatus,
        created: group.parent.created,
        noAccess: false,
        specifierType,
      };
    }
    return {
      fileId: group.parent.fileId,
      revisionId: group.parent.revisionId,
      pinnedRevisionId: trackedFile?.pinnedFileRevisionId ?? undefined,
      noAccess: true,
      specifierType,
    };
  });
}

export function configurationTrackedFilesToNewTrackedFiles(trackedFiles: ConfigurationTrackedFile[]): NewTrackedFile[] {
  return trackedFiles.map((item) => {
    const specifierType =
      item.specifierType === TrackedFileSpecifierType.Locked
        ? TrackedFileSpecifierType.Locked
        : TrackedFileSpecifierType.Latest;

    if (specifierType === TrackedFileSpecifierType.Locked && item.pinnedFileRevisionId) {
      return {
        specifierType,
        fileId: item.fileId,
        pinnedFileRevisionId: item.pinnedFileRevisionId,
      } as NewTrackedFile;
    }

    return {
      specifierType,
      fileId: item.fileId,
    } as NewTrackedFile;
  });
}
